// Paints the map overlay layers for the PNG export by mapping each layer type
// to the canvas primitives in mapShapes. Aggregation comes from mapLayerModel,
// so an exported image shows exactly what the Leaflet markers show.
import { PALETTES } from './mapIcons';
import {
  layerItems,
  valueFor,
  fractionsOf,
  resolveDonutColumns,
  itemDonutParts
} from './mapLayerModel';
import { drawDot, drawRing, drawDonut, drawBar, drawLabel, TRACK } from './mapShapes';

// Paint every visible layer. `project(lat, lon)` returns canvas pixels, so
// this module knows nothing about zoom or the view.
export function drawMapLayers(ctx, { layers, points, project, labelColor = '#1c1b1f' }) {
  for (const layer of layers || []) {
    if (!layer.visible) continue;
    drawLayer(ctx, layer, layerItems(points, layer.group), project, labelColor);
  }
}

function drawLayer(ctx, layer, items, project, labelColor) {
  const options = layer.options || {};
  const scale = Math.max(1, Number(options.size) || 1);
  const colors = palette(options.palette);
  switch (layer.type) {
    case 'dots':
      items.forEach((it) =>
        drawDot(ctx, project(it.lat, it.lon), Number(options.size) || 4, options.color || '#2e7d32')
      );
      break;
    case 'pie':
      paintFractions(ctx, layer, items, project, (point, fraction, index) =>
        drawRing(ctx, point, 16 * scale, 8 * scale, fraction, colors[index % colors.length], TRACK)
      );
      break;
    case 'bar':
      paintFractions(ctx, layer, items, project, (point, fraction) =>
        drawBar(ctx, point, fraction, scale, options.color || '#2e7d32')
      );
      break;
    case 'donuts':
      items.forEach((it) =>
        drawDonut(
          ctx,
          project(it.lat, it.lon),
          itemDonutParts(it, resolveDonutColumns(layer, items), options.labels),
          colors,
          scale
        )
      );
      break;
    case 'labels':
      items.forEach((it) =>
        drawLabel(ctx, project(it.lat, it.lon), labelOf(it, options.field), labelColor)
      );
      break;
    default:
      break;
  }
}

// Layers whose marker size comes from a value column: compute the fractions
// once, then paint every item with its share of the layer maximum.
function paintFractions(ctx, layer, items, project, paint) {
  const fractions = fractionsOf(items.map((it) => valueFor(it, layer)));
  items.forEach((it, i) => paint(project(it.lat, it.lon), fractions[i], i));
}

function palette(name) {
  return PALETTES[name] || PALETTES.default;
}

function labelOf(item, field) {
  const raw = field != null && item.members[0] ? item.members[0][field] : null;
  return raw == null ? item.label : String(raw);
}
